import React, { useState, useRef, useLayoutEffect } from 'react';
import { Card, ClickableItem, Column, Flex, Loader, Text, AssistChip } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { SlideFade } from '@upsales/components/animations';
import { useTranslation } from 'Components/Helpers/translate';
import './AIGeneratedContentCard.scss';

interface AIGeneratedContentCardProps {
	children: React.ReactNode;
	isLoading: boolean;
	title: string;
}

export const AIGeneratedContentCard = ({ children, isLoading, title }: AIGeneratedContentCardProps) => {
	const [isExpanded, setIsExpanded] = useState(true);
	const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
	const ref = useRef<HTMLDivElement>(null);
	const classes = new BemClass('AIGeneratedContentCard');
	const { t } = useTranslation();
	const toggle = () => setIsExpanded(!isExpanded);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const measure = () => setContentHeight(el.scrollHeight);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<Card borderRadius className={classes.b()}>
			<Flex
				alignItems="center"
				justifyContent="space-between"
				wrap="nowrap"
				className={classes.elem('header').mod({ clickable: !isLoading }).b()}
				onClick={() => {
					if (!isLoading) toggle();
				}}
				role="button"
				aria-expanded={isExpanded}
				tabIndex={0}
				onKeyDown={e => {
					if (isLoading) return;
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						toggle();
					}
				}}
			>
				<Flex alignItems="center" gap="u2" wrap="nowrap" className={classes.elem('title').b()}>
					<Flex alignItems="center" gap="u1" wrap="nowrap">
						<AssistChip
							className={classes.elem('chip').b()}
							icon="stars"
							type="purple"
							title={t('mail.upsalesAI')}
						/>
					</Flex>
					<Text bold className={classes.elem('titleText').b()}>
						{t(title)}
					</Text>
				</Flex>
				{!isLoading ? (
					<Column fixedWidth={50}>
						<span className={classes.elem('chevron').mod({ expanded: isExpanded }).b()}>
							<ClickableItem
								onClick={e => {
									e.stopPropagation();
									toggle();
								}}
								icon="chevron-down"
								borderRadius
								className={classes.elem('close').b()}
							/>
						</span>
					</Column>
				) : (
					<Loader noU size="xs" />
				)}
			</Flex>

			<SlideFade visible={isExpanded} speed="fast" height maxHeight={contentHeight}>
				<div className={classes.elem('contentWrapper').mod({ expanded: isExpanded }).b()}>
					<div ref={ref} className={classes.elem('content').b()}>
						{children}
					</div>
				</div>
			</SlideFade>
		</Card>
	);
};
